import { AdminGuard } from "@/admin/components/admin-guard";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { FloatingSelect } from "@/components/ui/floating-select";
import { Pagination } from "@/components/ui/pagination";
import { QueryError, queryErrorMessage } from "@/components/ui/query-error";
import { RelativeTime } from "@/components/ui/relative-time";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/use-auth";
import { api, edenFetch } from "@/lib/eden";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useNavigate, useSearch } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { userImageUrl } from "../../../../shared/image-url";
import { USER_IMAGE_SOURCE_CODES } from "../../../../shared/user-image-source-codes";

export const MODERATED_PAGE_SIZE = 30;

export type BlockedFilter = "blocked" | "allowed" | "all";

export function isBlockedFilter(value: unknown): value is BlockedFilter {
  return value === "blocked" || value === "allowed" || value === "all";
}

/** Whether a URL `source` value is one of the canonical upload sources. */
export function isImageSource(value: unknown): value is string {
  return USER_IMAGE_SOURCE_CODES.some((code) => code === value);
}

/**
 * One page of the moderation queue. Exported because moderated-image-detail re-runs the
 * very same query — same key, so it usually answers from cache — and fetches the
 * adjacent page when Previous / Next crosses a page boundary.
 */
export function moderatedImagesListQuery(opts: {
  blocked: BlockedFilter;
  source: string;
  /** 0-based. */
  page: number;
}) {
  return {
    queryKey: ["/api/admin/user-images", opts.blocked, opts.source, opts.page],
    queryFn: () =>
      edenFetch(
        api.api.admin["user-images"].get({
          query: {
            blocked: opts.blocked,
            ...(opts.source !== "all" ? { source: opts.source } : {}),
            limit: MODERATED_PAGE_SIZE,
            offset: opts.page * MODERATED_PAGE_SIZE,
          },
        }),
      ),
  };
}

export default function AdminModeratedImagesPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isDetailRoute = pathname !== "/admin/moderated-images";

  // Filters and page live in the URL (page is 1-based there, absent on the first page),
  // so Back out of an image lands on the same page of the same queue — and the detail
  // page can rebuild it for its Previous / Next.
  const search = useSearch({ from: "/admin/moderated-images/" });
  const blockedFilter = search.blocked ?? "blocked";
  const sourceFilter = search.source ?? "all";
  const page = (search.page ?? 1) - 1;
  const patchSearch = (patch: {
    blocked?: BlockedFilter;
    source?: string;
    /** 0-based, as the rest of the page counts. */
    page?: number;
  }) =>
    void navigate({
      from: "/admin/moderated-images/",
      to: "/admin/moderated-images",
      search: (prev) => ({
        ...prev,
        ...(patch.blocked !== undefined
          ? { blocked: patch.blocked === "blocked" ? undefined : patch.blocked }
          : {}),
        ...(patch.source !== undefined
          ? { source: patch.source === "all" ? undefined : patch.source }
          : {}),
        ...(patch.page !== undefined ? { page: patch.page > 0 ? patch.page + 1 : undefined } : {}),
      }),
    });

  // A row carries the queue it came from, so the detail page's Previous / Next walks
  // this same filtered page and can fetch across page boundaries.
  const openImage = (id: string) =>
    void navigate({
      to: "/admin/moderated-images/$id",
      params: { id },
      search: {
        lblocked: blockedFilter,
        lsource: sourceFilter === "all" ? undefined : sourceFilter,
        lpage: page > 0 ? page + 1 : undefined,
      },
    });

  const { data, isLoading, isError, error, refetch } = useQuery({
    // The moderation queue fills from uploads as they happen — keep it current.
    refetchInterval: 60_000,
    ...moderatedImagesListQuery({ blocked: blockedFilter, source: sourceFilter, page }),
    enabled: !!currentUser?.is_admin && !isDetailRoute,
  });

  const { data: stats } = useQuery({
    queryKey: ["/api/admin/user-images/stats"],
    queryFn: () => edenFetch(api.api.admin["user-images"].stats.get()),
    enabled: !!currentUser?.is_admin && !isDetailRoute,
  });

  if (isDetailRoute) return <Outlet />;

  return (
    <AdminGuard>
      <div className="flex flex-col h-full">
        <div className="border-b px-6 h-14 shrink-0 flex items-center">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" />
            Moderated Images
          </h1>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {stats && (
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-3">
                  <p className="text-2xl font-bold text-destructive">{stats.blocked}</p>
                  <p className="text-sm text-muted-foreground">Blocked</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <p className="text-2xl font-bold">{stats.allowed}</p>
                  <p className="text-sm text-muted-foreground">Allowed</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <FloatingSelect
              aria-label="Blocked filter"
              value={blockedFilter}
              onValueChange={(v) => patchSearch({ blocked: v as BlockedFilter, page: 0 })}
              className="w-40"
              items={[
                { value: "blocked", label: "Blocked" },
                { value: "allowed", label: "Allowed" },
                { value: "all", label: "All" },
              ]}
            />
            <FloatingSelect
              aria-label="Source filter"
              value={sourceFilter}
              onValueChange={(v) => patchSearch({ source: v, page: 0 })}
              className="w-48"
              items={[
                { value: "all", label: "All Sources" },
                ...USER_IMAGE_SOURCE_CODES.map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>

          {isError ? (
            <QueryError
              message={queryErrorMessage("the moderated images", error)}
              onRetry={() => void refetch()}
            />
          ) : isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-md" />
              ))}
            </div>
          ) : !data || data.images.length === 0 ? (
            <EmptyState
              icon="shield-alert"
              title="No images found"
              message={
                blockedFilter === "blocked"
                  ? "No blocked uploads. Either nothing has been moderated or the filters are too narrow."
                  : "Try adjusting your filters."
              }
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Image</TableHead>
                    <TableHead>Uploader</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Top Category</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Uploaded</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.images.map((img) => {
                    const conf = img.moderation_top_confidence;
                    const confLabel = conf != null ? `${Math.round(conf)}%` : "—";
                    return (
                      <TableRow
                        key={img.user_image_id}
                        className="cursor-pointer hover-elevate"
                        tabIndex={0}
                        onClick={() => openImage(img.user_image_id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openImage(img.user_image_id);
                          }
                        }}
                      >
                        <TableCell>
                          <img
                            src={userImageUrl(img.user_image_id, {
                              width: 96,
                              height: 96,
                              fit: "cover",
                            })}
                            alt=""
                            className="w-12 h-12 rounded object-cover bg-muted"
                            loading="lazy"
                          />
                        </TableCell>
                        <TableCell className="text-base">
                          {img.uploader_display_name || img.uploader_username || "Unknown"}
                          {img.uploader_is_banned && (
                            <Badge variant="destructive" className="ml-2">
                              Banned
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {img.is_blocked ? (
                            <Badge variant="destructive">Blocked</Badge>
                          ) : (
                            <Badge variant="secondary">Allowed</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-base">
                          {img.moderation_top_category ? (
                            <span>
                              {img.moderation_top_category}
                              <span className="text-muted-foreground ml-1">{confLabel}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-base">
                          <span className="text-muted-foreground">
                            {img.user_image_source_code}
                          </span>
                        </TableCell>
                        <TableCell className="text-base text-muted-foreground">
                          <RelativeTime value={img.created_at} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Pagination
                page={page}
                pageSize={MODERATED_PAGE_SIZE}
                total={data.total}
                onPageChange={(next) => patchSearch({ page: next })}
                showRange={false}
              />
            </>
          )}
        </div>
      </div>
    </AdminGuard>
  );
}
